import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError, formatINR } from "@/lib/api";
import { PageHeader, Spinner, Card, Table, PrimaryButton, GhostButton, EmptyState } from "@/components/common";
import { toast } from "sonner";
import { GraduationCap, Upload, Download, Users, CheckCircle2, AlertTriangle, IndianRupee, Plus } from "lucide-react";

export default function SchoolProgram() {
  const [inst, setInst] = useState(undefined); // undefined=loading, null=none
  const [fee, setFee] = useState(0);
  const [enroll, setEnroll] = useState([]);
  const [students, setStudents] = useState([]);
  const [preview, setPreview] = useState(null); // {valid, error_count, errors, _csv}
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const loadAll = async (iid) => {
    const [e, s] = await Promise.all([
      api.get(`/institutions/${iid}/enrollment`).then((r) => r.data).catch(() => []),
      api.get(`/institutions/${iid}/students`).then((r) => r.data).catch(() => []),
    ]);
    setEnroll(e); setStudents(s);
  };

  useEffect(() => {
    api.get("/admin/program-config").then((r) => setFee(r.data?.per_student_fee || 0)).catch(() => {});
    api.get("/institutions").then((r) => {
      const i = (r.data || [])[0] || null;
      setInst(i);
      if (i) loadAll(i.id);
    }).catch(() => setInst(null));
  }, []);

  if (inst === undefined) return <Spinner />;

  const enrolled = enroll[0];

  const doEnroll = async () => {
    setBusy(true);
    try { await api.post(`/institutions/${inst.id}/enroll`, {}); toast.success("Enrolled in the annual program"); await loadAll(inst.id); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get(`/institutions/${inst.id}/students/template`);
      const blob = new Blob([data.csv], { type: "text/csv" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "spora1-students-template.csv"; a.click();
    } catch (e) { toast.error(apiError(e)); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const text = await file.text();
    setBusy(true);
    try {
      const { data } = await api.post(`/institutions/${inst.id}/students/import`, { csv_text: text, commit: false });
      setPreview({ ...data, _csv: text });
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const confirmImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/institutions/${inst.id}/students/import`, { csv_text: preview._csv, commit: true });
      toast.success(`Imported ${data.inserted} student(s)`);
      setPreview(null); await loadAll(inst.id);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="animate-fadeUp">
      <PageHeader title="School Sports Program" subtitle="Annual enrolment, student roster & SPORA IDs" />

      {!inst ? (
        <EmptyState icon={GraduationCap} title="No institution yet" text="Register your school or institution first, then return here to enrol in the annual sports program and upload your student roster."
          action={<Link to="/app/institutions"><PrimaryButton data-testid="goto-institutions-btn"><Plus className="h-4 w-4" /> Register Institution</PrimaryButton></Link>} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Card>
              <div className="mb-1 flex items-center gap-2"><GraduationCap className="h-4 w-4 text-primary" /><p className="font-mono text-xs uppercase text-muted-foreground">Program status</p></div>
              {enrolled ? (
                <p className="font-display text-xl font-bold capitalize text-emerald-600" data-testid="program-status">{enrolled.status} · {enrolled.year}</p>
              ) : (
                <PrimaryButton data-testid="enroll-btn" className="mt-1" onClick={doEnroll} disabled={busy}>Enrol for {new Date().getFullYear()}</PrimaryButton>
              )}
            </Card>
            <Card>
              <div className="mb-1 flex items-center gap-2"><Users className="h-4 w-4 text-primary" /><p className="font-mono text-xs uppercase text-muted-foreground">Enrolled students</p></div>
              <p className="font-display text-2xl font-bold" data-testid="student-count">{students.length}</p>
            </Card>
            <Card>
              <div className="mb-1 flex items-center gap-2"><IndianRupee className="h-4 w-4 text-primary" /><p className="font-mono text-xs uppercase text-muted-foreground">Annual fee due</p></div>
              <p className="font-display text-2xl font-bold" data-testid="fee-due">{fee > 0 ? formatINR(students.length * fee) : "Fee not set"}</p>
              {fee > 0 && <p className="text-xs text-muted-foreground">{students.length} × {formatINR(fee)}/student</p>}
            </Card>
          </div>

          <Card className="mt-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-display text-lg font-semibold">Bulk student upload</h3>
                <p className="text-sm text-muted-foreground">Download the template, fill it in Excel, save as CSV and upload.</p>
              </div>
              <div className="flex gap-2">
                <GhostButton data-testid="download-template-btn" onClick={downloadTemplate}><Download className="h-4 w-4" /> Template</GhostButton>
                <PrimaryButton data-testid="upload-students-btn" onClick={() => fileRef.current?.click()} disabled={busy}><Upload className="h-4 w-4" /> Upload CSV</PrimaryButton>
                <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="students-file-input" onChange={onFile} />
              </div>
            </div>

            {preview && (
              <div className="mt-4 rounded-xl border border-border bg-background/40 p-4" data-testid="import-preview">
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="flex items-center gap-1.5 font-semibold text-emerald-600"><CheckCircle2 className="h-4 w-4" /> {preview.valid} ready to import</span>
                  {preview.error_count > 0 && <span className="flex items-center gap-1.5 font-semibold text-rose-600"><AlertTriangle className="h-4 w-4" /> {preview.error_count} row(s) with errors</span>}
                </div>
                {preview.errors?.length > 0 && (
                  <ul className="mt-3 max-h-40 space-y-1 overflow-y-auto text-xs text-muted-foreground">
                    {preview.errors.map((er, i) => <li key={i}>Row {er.row}: {er.errors.join(", ")}</li>)}
                  </ul>
                )}
                <div className="mt-4 flex gap-2">
                  <PrimaryButton data-testid="confirm-import-btn" onClick={confirmImport} disabled={busy || preview.valid === 0}>Confirm import ({preview.valid})</PrimaryButton>
                  <GhostButton data-testid="cancel-import-btn" onClick={() => setPreview(null)}>Cancel</GhostButton>
                </div>
              </div>
            )}
          </Card>

          <h3 className="mb-3 mt-6 font-display text-xl font-semibold">Student roster</h3>
          {students.length === 0 ? (
            <Card className="text-center text-sm text-muted-foreground">No students enrolled yet.</Card>
          ) : (
            <Table head={["Name", "Class", "Admission #", "Sports", "SPORA ID"]}>
              {students.map((s) => (
                <tr key={s.id} data-testid={`student-row-${s.id}`} className="border-b border-border/60 last:border-0 hover:bg-background/40">
                  <td className="px-4 py-3 font-medium">{s.student_name}</td>
                  <td className="px-4 py-3 text-sm">{s.klass}{s.section ? `-${s.section}` : ""}</td>
                  <td className="px-4 py-3 font-mono text-xs">{s.admission_no}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{s.sport_interests || "-"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-primary">{s.spora_id}</td>
                </tr>
              ))}
            </Table>
          )}
        </>
      )}
    </div>
  );
}
